'use client';

import * as React from 'react';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { TableRow, TableCell } from '@/components/ui/table';

interface PaymentsTableSkeletonProps {
  rows?: number;
}

/**
 * PaymentsTableSkeleton component for displaying loading state
 * Returns only table rows to be inserted into existing table structure
 */
export function PaymentsTableSkeleton({ rows = 8 }: PaymentsTableSkeletonProps) {
  return (
    <SkeletonTheme baseColor="#f8fafc" highlightColor="#f1f5f9" borderRadius="0.375rem">
      <>
        {Array.from({ length: rows }, (_, index) => (
          <TableRow key={index}>
            {/* Select Checkbox */}
            <TableCell className="w-10">
              <Skeleton height={16} width={16} />
            </TableCell>

            {/* Payment ID Badge */}
            <TableCell>
              <Skeleton height={24} width={90} borderRadius="0.75rem" />
            </TableCell>

            {/* Customer (two-line: name + email) */}
            <TableCell className="w-[200px]">
              <div className="flex flex-col gap-0.5 py-1">
                <Skeleton height={14} width={130} />
                <Skeleton height={12} width={150} />
              </div>
            </TableCell>

            {/* Amount (two-line: money + currency) */}
            <TableCell className="w-[120px]">
              <div className="flex flex-col gap-0.5 py-1">
                <Skeleton height={16} width={70} />
                <Skeleton height={12} width={35} />
              </div>
            </TableCell>

            {/* Status Badge */}
            <TableCell className="w-24">
              <Skeleton height={20} width={65} borderRadius="0.375rem" />
            </TableCell>

            {/* Payment Method (CardBadge) */}
            <TableCell className="w-32">
              <div className="flex items-center gap-1.5">
                <Skeleton height={20} width={45} borderRadius="0.375rem" />
                <Skeleton height={14} width={40} />
              </div>
            </TableCell>

            {/* Date (two-line: date + time) */}
            <TableCell className="w-[130px]">
              <div className="flex flex-col gap-0.5 py-1">
                <Skeleton height={14} width={80} />
                <Skeleton height={12} width={60} />
              </div>
            </TableCell>

            {/* Store */}
            <TableCell className="w-[150px]">
              <Skeleton height={14} width={90} />
            </TableCell>

            {/* Currency Badge */}
            <TableCell className="w-[100px]">
              <Skeleton height={20} width={45} borderRadius="0.375rem" />
            </TableCell>

            {/* Processor Badge */}
            <TableCell className="w-[130px]">
              <Skeleton height={20} width={70} borderRadius="0.375rem" />
            </TableCell>

            {/* Initiated By */}
            <TableCell className="w-32">
              <Skeleton height={14} width={80} />
            </TableCell>

            {/* Chain Index Badge */}
            <TableCell className="w-[100px]">
              <Skeleton height={20} width={24} borderRadius="0.375rem" />
            </TableCell>

            {/* Retry Index Badge */}
            <TableCell className="w-[100px]">
              <Skeleton height={20} width={24} borderRadius="0.375rem" />
            </TableCell>

            {/* Reason Type Badge */}
            <TableCell className="w-[130px]">
              <Skeleton height={20} width={60} borderRadius="0.375rem" />
            </TableCell>

            {/* Action Required Badge */}
            <TableCell className="w-[150px]">
              <Skeleton height={20} width={50} borderRadius="0.375rem" />
            </TableCell>

            {/* Actions */}
            <TableCell className="w-10">
              <Skeleton height={32} width={32} borderRadius="0.25rem" />
            </TableCell>
          </TableRow>
        ))}
      </>
    </SkeletonTheme>
  );
}
